import { memo, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  AudioScrubber,
  Button,
  Chip,
  PopoverDialog,
  SpinningLoader,
  Text,
  TextLink,
  useAlert,
  usePopoverDialog,
} from '@frontend/design-system';
import { getNameWithoutExtension } from '../../../../../utils/phone-utils';
import { useGenerateTTSMutation } from '../../../../audio-picker/use-audio-mutations';
import { logError } from '../../../helpers/utils';
import { getAiReceptionistDefaultParams } from '../../add-step-panel/weave-assistant-step';
import { AiReceptionistCreditNotifications } from '../../ai-receptionist-credit-notifications';
import {
  hasCallGroupExpansion,
  hasCallQueueExpansion,
  hasCallRouteExpansion,
  hasDeviceExpansion,
  hasForwardingNumberExpansion,
  hasPhoneHoursExpansion,
  hasPhoneTreeExpansion,
  hasPlayMessageExpansion,
  hasVoicemailExpansion,
  hasWeaveAssistantExpansion,
  SharedNodeProps,
} from '../data';
import { BaseRoutingNode } from './base-routing-node';

export const CallGroupNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasCallGroupExpansion(data.callObject) ? data.callObject : undefined;

    const [ringtonesEnabled] = PhoneConfigOrg.useWetag('callgroup-ringtones');

    const ringtone = callObject?.callGroupExpansion.ringtone;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-group'
        title='Call Group'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Caller Label')} label={callObject?.callGroupExpansion.callerLabel || '--'} />

          <DevicesLabelItem deviceNames={callObject?.callGroupExpansion.deviceNames ?? []} />

          {ringtonesEnabled && (
            <LabelItem title={t('Desk Phone Ringtone')} label={ringtone?.displayName || t('Default')} />
          )}
        </div>
      </BaseRoutingNode>
    );
  }
);
CallGroupNode.displayName = 'CallGroupNode';

export const CallQueueNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasCallQueueExpansion(data.callObject) ? data.callObject : undefined;

    const [ringtonesEnabled] = PhoneConfigOrg.useWetag('callqueue-device-ringtones');

    const ringtone = callObject?.callQueueExpansion.ringtone;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-queue'
        title='Call Queue'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(1)};
            `}
          >
            <LabelItem title={t('Caller Label')} label={callObject?.callQueueExpansion.callerLabel || '--'} />

            <DevicesLabelItem deviceNames={callObject?.callQueueExpansion.deviceNames ?? []} />

            <LabelItem
              title={t('Greeting')}
              label={getNameWithoutExtension(callObject?.callQueueExpansion.greetingMediaFileName) || '--'}
            />

            {AudioPlayerComponent &&
              callObject?.callQueueExpansion.greetingMediaItemId &&
              callObject?.callQueueExpansion.downloadUrl && (
                <AudioPlayerComponent mediaItemId={callObject?.callQueueExpansion.greetingMediaItemId} />
              )}

            {ringtonesEnabled && (
              <LabelItem title={t('Desk Phone Ringtone')} label={ringtone?.displayName || t('Default')} />
            )}
          </div>
        </>
      </BaseRoutingNode>
    );
  }
);
CallQueueNode.displayName = 'CallQueueNode';

export const CallRouteNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const { getScopeName, isSingleTypeScope } = useAppScopeStore();
    const { getDialogProps, getTriggerProps } = usePopoverDialog();

    const callObject = hasCallRouteExpansion(data.callObject) ? data.callObject : undefined;

    const locationName = getScopeName(callObject?.callRouteExpansion.locationId ?? '');

    const formattedPhoneNumbers = useMemo(
      () => callObject?.callRouteExpansion.phoneNumbers?.map((item) => formatPhoneNumber(item)) ?? [],
      [callObject?.callRouteExpansion.phoneNumbers]
    );
    const isDeviceExtension = type === CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-route'
        title={isDeviceExtension ? t('Device Route') : t('Call Route')}
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <div>
            <Text size='small' color='subdued'>
              {t('Phone Numbers')}
            </Text>
            {formattedPhoneNumbers.length > 0 ? (
              <>
                <TextWithMore
                  text={formattedPhoneNumbers[0]}
                  count={formattedPhoneNumbers.length}
                  popoverTriggerProps={getTriggerProps}
                />
                <PopoverList
                  listNames={formattedPhoneNumbers}
                  getDialogProps={getDialogProps}
                  title={t('Phone Numbers')}
                />
              </>
            ) : (
              <>--</>
            )}
          </div>

          <LabelItem title={t('Extensions')} label={callObject?.callRouteExpansion.extensions?.join(', ') || '--'} />

          {!isSingleTypeScope && (
            <div>
              <Text size='small' color='subdued'>
                {t('Location')}
              </Text>
              <Chip.SingleChip>{locationName || '--'}</Chip.SingleChip>
            </div>
          )}
        </div>
      </BaseRoutingNode>
    );
  }
);
CallRouteNode.displayName = 'CallRouteNode';

export const ForwardingDeviceNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const callObject = hasDeviceExpansion(data.callObject) ? data.callObject : undefined;

    const ringDuration = (callObject?.deviceExpansion.timeout ?? 0) / 1000;
    const ringDurationText = ringDuration ? `${t('{{duration}} seconds', { duration: ringDuration })}` : '--';

    const deviceType = callObject?.deviceExpansion.deviceType;
    const isMobileApp = deviceType === DeviceType_Enum.MOBILE_APP;
    const isDeskPhone = deviceType === DeviceType_Enum.DESK_PHONE;

    const displayName = useMemo(() => {
      if (data.label.startsWith('Forward Device (') && data.label.endsWith(')')) {
        return callObject?.deviceExpansion?.callerLabel || 'Forward Device';
      }
      return data.label;
    }, [data.label, callObject?.deviceExpansion?.callerLabel]);

    const ringtone = callObject?.deviceExpansion.ringtone;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='cell-phone'
        title='Device'
        label={displayName}
        warning={data.warning}
        linkId={isMobileApp ? undefined : data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Ring Duration')} label={ringDurationText} />

          <LabelItem title={t('Caller Label')} label={callObject?.deviceExpansion.callerLabel || '--'} />
          {isDeskPhone && <LabelItem title={t('Desk Phone Ringtone')} label={ringtone?.displayName || t('Default')} />}
        </div>
      </BaseRoutingNode>
    );
  }
);
ForwardingDeviceNode.displayName = 'ForwardingDeviceNode';

export const ForwardingNumberNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const callObject = hasForwardingNumberExpansion(data.callObject) ? data.callObject : undefined;

    const ringDuration = (callObject?.forwardingNumberExpansion?.timeout ?? 0) / 1000;
    const ringDurationText = ringDuration
      ? `${t('{{duration}} seconds', { duration: ringDuration })}`
      : t('Ring until answered');
    const callerNumber = callObject?.forwardingNumberExpansion?.callerNumber;
    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='send-forward'
        title='Forwarding Number'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Ring Duration')} label={ringDurationText} />
          <LabelItem
            title={t('Forwarded Caller ID Number')}
            label={
              callerNumber === '0' ? t('Caller Number') : formatPhoneNumber(callerNumber ?? '') || t('Caller Number')
            }
          />
        </div>
      </BaseRoutingNode>
    );
  }
);
ForwardingNumberNode.displayName = 'ForwardingNumberNode';

export const OfficeHoursNode = memo(
  ({
    data,
    type,
    canEdit,
    hoursManagementPermission,
    onCallObjectLinkClick,
    DayHoursListComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const callObject = hasPhoneHoursExpansion(data.callObject) ? data.callObject : undefined;
    const phoneHours = callObject?.phoneHoursExpansion?.phoneHours ?? [];

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='clock'
        title='Phone Hours'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit && hoursManagementPermission}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        {DayHoursListComponent && !!phoneHours.length && (
          <div
            // Need to apply the "nowheel" to allow scrolling inside the custom node
            // From react-flow docs: "If you have scroll containers inside your custom node
            // you can add the class nowheel to disable the default canvas pan behaviour when
            // scrolling inside your custom nodes."
            className='nowheel'
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              height: 316px;
              overflow-y: scroll;
            `}
          >
            <DayHoursListComponent phoneHours={phoneHours} />
          </div>
        )}
      </BaseRoutingNode>
    );
  }
);
OfficeHoursNode.displayName = 'OfficeHoursNode';

export const PhoneTreeNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasPhoneTreeExpansion(data.callObject) ? data.callObject : undefined;
    const { timeout, maxFailures, maxTimeouts, promptMediaFileName, promptMediaItemId } =
      callObject?.phoneTreeExpansion || {};

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='phone-tree'
        title='Phone Tree'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Phone Tree Prompt')} label={promptMediaFileName || '--'} />

          {AudioPlayerComponent && promptMediaItemId ? (
            <AudioPlayerComponent mediaItemId={promptMediaItemId} />
          ) : undefined}

          <LabelItem title={t('Timeout After')} label={((timeout ?? 0) / 1000).toFixed(0) + ' seconds'} />

          <LabelItem title={t('Max Timeouts')} label={maxTimeouts || '--'} />

          <LabelItem title={t('Max Invalid Selections')} label={maxFailures || '--'} />
        </div>
      </BaseRoutingNode>
    );
  }
);
PhoneTreeNode.displayName = 'PhoneTreeNode';

export const PlayNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const callObject = hasPlayMessageExpansion(data.callObject) ? data.callObject : undefined;
    const mediaItemId = callObject?.playMessageExpansion?.mediaItemId;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='speaker'
        title='Play Message'
        label={getNameWithoutExtension(data.label)}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        {AudioPlayerComponent && mediaItemId ? <AudioPlayerComponent mediaItemId={mediaItemId} /> : undefined}
      </BaseRoutingNode>
    );
  }
);
PlayNode.displayName = 'PlayNode';

export const VoicemailNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasVoicemailExpansion(data.callObject) ? data.callObject : undefined;
    const greetingId = callObject?.voicemailExpansion?.greetingMediaItemId;
    const downloadUrl = callObject?.voicemailExpansion?.downloadUrl;
    const { getDialogProps, getTriggerProps } = usePopoverDialog();
    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='voicemail'
        title={'Voicemail'}
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem
            title={t('Greeting')}
            label={getNameWithoutExtension(callObject?.voicemailExpansion?.greetingMediaFileName) || '--'}
          />
          {AudioPlayerComponent && greetingId && downloadUrl && (
            <AudioPlayerComponent mediaItemId={greetingId} downloadUrl={downloadUrl} />
          )}
          <LabelItem
            title={t('Share in App')}
            label={callObject?.voicemailExpansion?.sharedAccess ? 'Enabled' : 'Disabled'}
          />
          <div>
            <LabelItem
              title={t('Email & SMS Notifications')}
              label={callObject?.voicemailExpansion?.sendNotification ? 'Enabled' : 'Disabled'}
            />
            {callObject?.voicemailExpansion?.sendNotification && (
              <NotificationDestinations
                emailAddresses={callObject?.voicemailExpansion?.emailAddresses ?? []}
                smsNumbers={callObject?.voicemailExpansion?.smsNumbers ?? []}
                getTriggerProps={getTriggerProps}
                getDialogProps={getDialogProps}
              />
            )}
          </div>
        </div>
      </BaseRoutingNode>
    );
  }
);
VoicemailNode.displayName = 'VoicemailNode';

export const RepeatNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='automated-message'
        title={'Repeat Phone Tree'}
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      />
    );
  }
);
RepeatNode.displayName = 'RepeatNode';

export const WeaveAssistantNode = memo(
  ({
    data,
    type,
    canEdit,
    onDeleteIconClick,
    onEditIconClick,
    locationCreditDetails,
    callRoutePhoneNumberLocationIds,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const alerts = useAlert();

    const callObject = hasWeaveAssistantExpansion(data.callObject) ? data.callObject : undefined;

    const greetingScript = callObject?.weaveAssistantExpansion?.greetingScript || '';
    const placeholderScript = callObject?.weaveAssistantExpansion?.placeholderScript || '';
    const fullMessage = (placeholderScript + ' ' + greetingScript).trim();

    const generateMutation = useGenerateTTSMutation();

    const [audioSrc, setAudioSrc] = useState('');

    const handleGenerateAudio = () => {
      generateMutation.mutate(getAiReceptionistDefaultParams(fullMessage), {
        onSuccess: (audioData) => {
          const audioUrl = URL.createObjectURL(audioData);
          setAudioSrc(audioUrl);
        },
        onError: () => {
          alerts.error(t('Failed to generate audio.'));
          logError(
            'Failed to generate TTS audio for AI Receptionist node on node Expansion. PC-8dh3j82',
            'WeaveAssistantNode',
            {
              nodeId: data.id,
              audioSrc,
              greetingScript,
              placeholderScript,
            }
          );
        },
      });
    };

    // If the greetingScript changes, regenerate the audio.
    useEffect(() => {
      if (greetingScript.length === 0) {
        setAudioSrc('');
        return;
      }
      handleGenerateAudio();
    }, [greetingScript]);

    const handleNodeExpanded = (isExpanded: boolean) => {
      if (isExpanded && !audioSrc && fullMessage.length > 0) {
        // Generate TTS audio when the node is expanded and there's no existing audio source.
        handleGenerateAudio();
      }
    };

    const creditNotifications = (
      <AiReceptionistCreditNotifications
        locationCreditDetails={locationCreditDetails}
        locationIds={callRoutePhoneNumberLocationIds ?? []}
      />
    );

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='aiweave-assistant'
        title={t('AI Receptionist')}
        label={''}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickEdit={onEditIconClick}
        onClickDelete={onDeleteIconClick}
        onNodeExpanded={handleNodeExpanded}
        additionalNotifications={creditNotifications}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Greeting')} />

          {generateMutation.isLoading ? (
            <SpinningLoader size='small' />
          ) : !!fullMessage.length && !!audioSrc ? (
            <AudioScrubber
              alwaysShowProgressButton
              autoSize
              showCurrentTime
              singlePlayer
              src={audioSrc}
              autoplay={false}
            />
          ) : (
            <Text>{t('No Greeting Audio')}</Text>
          )}
        </div>
      </BaseRoutingNode>
    );
  }
);
WeaveAssistantNode.displayName = 'WeaveAssistantNode';

const LabelItem = ({ title, label }: { title: string; label?: string | number }) => {
  return (
    <div>
      <Text size='small' color='subdued'>
        {title}
      </Text>
      <Text size='medium'>{label}</Text>
    </div>
  );
};

const DevicesLabelItem = ({ deviceNames }: { deviceNames: string[] }) => {
  const { t } = useTranslation('phone');
  const { getDialogProps, getTriggerProps } = usePopoverDialog();

  return (
    <div>
      <Text size='small' color='subdued'>
        {t('Devices')}
      </Text>

      <div
        css={css`
          display: flex;
          align-items: center;
          position: relative;
          bottom: ${theme.spacing(0.5)};
        `}
      >
        <Text size='medium'>{t('{{count}} Devices', { count: deviceNames.length })}</Text>
        {!!deviceNames.length && <Button iconName='preview' variant='secondary' {...getTriggerProps()} />}
      </div>

      <PopoverList getDialogProps={getDialogProps} listNames={deviceNames} title={t('Assigned Devices')} />
    </div>
  );
};

const NotificationDestinations = ({
  emailAddresses,
  smsNumbers,
  getTriggerProps,
  getDialogProps,
}: {
  emailAddresses: string[];
  smsNumbers: string[];
  getTriggerProps: ReturnType<typeof usePopoverDialog>['getTriggerProps'];
  getDialogProps: ReturnType<typeof usePopoverDialog>['getDialogProps'];
}) => {
  const { t } = useTranslation('phone');

  const emailCount = emailAddresses.length;
  const smsCount = smsNumbers.length;

  const parts = [];
  if (emailCount > 0) {
    parts.push(`${emailCount} ${emailCount === 1 ? 'email' : 'emails'}`);
  }
  if (smsCount > 0) {
    parts.push(`${smsCount} SMS ${smsCount === 1 ? 'number' : 'numbers'}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        position: relative;
        bottom: ${theme.spacing(0.5)};
      `}
    >
      <Text size='medium'>{parts.join(', ')}</Text>

      {(emailCount > 0 || smsCount > 0) && <Button iconName='preview' variant='secondary' {...getTriggerProps()} />}

      <PopoverList
        getDialogProps={getDialogProps}
        listNames={[...emailAddresses, ...smsNumbers.map((number) => formatPhoneNumber(number))]}
        title={t('Notification Destinations:')}
      />
    </div>
  );
};

const PopoverList = ({
  getDialogProps,
  listNames,
  title,
}: {
  listNames: string[];
  getDialogProps: ReturnType<typeof usePopoverDialog>['getDialogProps'];
  title: string;
}) => {
  return (
    <PopoverDialog
      {...getDialogProps()}
      css={css`
        border-radius: ${theme.borderRadius.medium};
        overflow-y: auto;
      `}
    >
      <div
        css={css`
          width: 248px;
          max-height: 368px;
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(0.5)};
        `}
      >
        <Text size='small' color='light'>
          {title}
        </Text>
        <ul
          css={css`
            border-radius: ${theme.borderRadius.medium};
            border: 1px solid ${theme.colors.neutral20};
            padding: 0;

            li:not(:last-child) {
              border-bottom: 1px solid ${theme.colors.neutral20};
            }
          `}
        >
          {listNames.map((name) => (
            <li
              key={name}
              css={css`
                list-style: none;
                padding: ${theme.spacing(1)};
              `}
            >
              <Text key={name} size='medium'>
                {name}
              </Text>
            </li>
          ))}
        </ul>
      </div>
    </PopoverDialog>
  );
};

const TextWithMore = ({
  text,
  count,
  onClick,
  trackingId,
  popoverTriggerProps,
}: {
  text: string;
  count: number;
  onClick?: MouseEventHandler<HTMLElement>;
  popoverTriggerProps?: ReturnType<typeof usePopoverDialog>['getTriggerProps'];
  trackingId?: string;
}) => {
  const { t } = useTranslation('phone');
  return (
    <Text size='medium'>
      {text}
      {count > 1 && (
        <TextLink
          css={css`
            margin-left: ${theme.spacing(0.5)};
          `}
          onClick={onClick}
          trackingId={trackingId}
          {...popoverTriggerProps?.()}
        >
          {t('+{{count}} more', { count: count - 1 })}
        </TextLink>
      )}
    </Text>
  );
};
